import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft, FaTrash } from "react-icons/fa";
import { ChevronDown, ChevronUp } from "lucide-react";
import { message, Spin, Select, Switch } from "antd";

import ReusableHeader from "../../../../../reusableField/ReusableHeader";
import DeleteIcon from "../../../../../Images/deleteIcon.png";
import "./AddQuize.css";

import {
  createDemographicQuestion,
  getDemographicQuestionById,
  updateDemographicQuestion,
  getAssessmentConfigurationOptions,
} from "../../../../../services/SyncApis";

let quizIdCounter = 1;

/* =========================================================
   EMPTY QUIZ
   ========================================================= */

const makeEmptyQuiz = () => ({
  id: quizIdCounter++,
  quiz: "",
  questionTypeId: "",
  options: ["", ""],
  optionIds: [null, null],
  optionWeightages: ["", ""],
  correctAnswers: [],
  isOpen: true,
  isNew: true,
});

const AddQuize = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const fullState = location.state || {};

  const roleId = fullState.roleId;
  const accountId = fullState.accountId;
  const questionId = fullState.questionId;
  const [isActive, setIsActive] = useState(true);

  const editMode =
    fullState.editMode || false;

  /* =========================================================
     QUESTION TYPES FROM BACKEND
     ========================================================= */

  const [
    questionTypes,
    setQuestionTypes,
  ] = useState([]);

  const [
    loadingOptions,
    setLoadingOptions,
  ] = useState(false);

  /* =========================================================
     QUIZZES
     ========================================================= */

  const [
    quizzes,
    setQuizzes,
  ] = useState([
    makeEmptyQuiz(),
  ]);

  /* =========================================================
     WEIGHTAGE
     ========================================================= */

  const [
    weightage,
    setWeightage,
  ] = useState("");

  const [
    weightageError,
    setWeightageError,
  ] = useState("");

  /* =========================================================
     UI STATE
     ========================================================= */

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    loadingEdit,
    setLoadingEdit,
  ] = useState(false);

  /* =========================================================
     ERRORS
     ========================================================= */

  const [
    errors,
    setErrors,
  ] = useState({
    quizzes: [
      {
        quiz: "",
        questionType: "",
        correctAnswer: "",
        options: [
          "",
          "",
        ],
        optionWeightages: ["", ""],
      },
    ],
  });
  const removeOption = (qIndex, optIndex) => {
    setQuizzes((prevQuizzes) =>
      prevQuizzes.map((quiz, index) => {
        if (index !== qIndex || quiz.options.length <= 2) {
          return quiz;
        }

        return {
          ...quiz,
          options: quiz.options.filter(
            (_, index) => index !== optIndex
          ),
          optionIds: quiz.optionIds.filter(
            (_, index) => index !== optIndex
          ),
          optionWeightages: quiz.optionWeightages.filter(
            (_, index) => index !== optIndex
          ),
          correctAnswers: (quiz.correctAnswers || [])
            .filter((index) => index !== optIndex)
            .map((index) => index > optIndex ? index - 1 : index),
        };
      })
    );
  };

  /* =========================================================
     LOAD ASSESSMENT OPTIONS
     ========================================================= */

  useEffect(() => {
    const loadQuestionTypes =
      async () => {
        setLoadingOptions(true);

        try {
          const response =
            await getAssessmentConfigurationOptions(
              Number(roleId),
              Number(accountId)
            );

          const data =
            response?.data || {};

          setQuestionTypes(
            Array.isArray(
              data.questionTypes
            )
              ? data.questionTypes
              : []
          );
        } catch (error) {
          console.error(
            "Error loading assessment configuration options:",
            error
          );

          message.error(
            "Failed to load question types."
          );
        } finally {
          setLoadingOptions(false);
        }
      };

    if (roleId && accountId) {
      loadQuestionTypes();
    }
  }, [roleId, accountId]);

  /* =========================================================
     QUESTION TYPE HELPERS
     ========================================================= */

  const getQuestionTypeById =
    (id) => {
      return questionTypes.find(
        (type) =>
          Number(type.id) ===
          Number(id)
      );
    };

  const getQuestionTypeValue =
    (id) => {
      return getQuestionTypeById(
        id
      )?.value;
    };

  /* =========================================================
     DEFAULT OPTIONS BY QUESTION TYPE
     ========================================================= */

  const getOptionsForQuestionType = (questionTypeId) => {
    const selectedType = getQuestionTypeById(questionTypeId);

    const typeValue = String(
      selectedType?.value || ""
    )
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_")
      .replace(/\//g, "_");

    const typeName = String(
      selectedType?.name || ""
    )
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_")
      .replace(/\//g, "_");

    const isYesNo =
      typeValue === "YES_NO" ||
      typeName === "YES_NO";

    if (isYesNo) {
      return ["True", "False"];
    }

    return ["", ""];
  };

  /* =========================================================
     FETCH QUESTION DATA IN EDIT MODE
     ========================================================= */

  useEffect(() => {
    if (
      !editMode ||
      !questionId ||
      !roleId
    ) {
      return;
    }

    if (!accountId) {
      message.error(
        "Admin client ID is missing."
      );

      return;
    }

    /*
     * Wait until backend question
     * types are loaded.
     */
    if (
      questionTypes.length === 0
    ) {
      return;
    }

    const loadQuestion =
      async () => {
        setLoadingEdit(true);

        try {
          const response =
            await getDemographicQuestionById(
              Number(roleId),
              questionId,
              Number(accountId),
            );

          const questionData =
            response?.data ||
            response;

          if (!questionData) {
            throw new Error(
              "Question data not found."
            );
          }

          setIsActive(
            questionData.isActive !== false
          );

          const questionText =
            questionData.question ??
            questionData.title ??
            questionData.quiz ??
            "";

          const questionTypeId =
            Number(
              questionData.questionTypeId
            );

          const apiOptions =
            Array.isArray(
              questionData.options
            )
              ? questionData.options
              : [];

          let optionTexts = [];
          let optionIds = [];

          const questionTypeValue =
            getQuestionTypeValue(
              questionTypeId
            );

          if (
            questionTypeValue ===
            "YES_NO"
          ) {
            optionTexts = [
              "True",
              "False",
            ];

            optionIds = [
              apiOptions?.[0]?._id ??
              null,
              apiOptions?.[1]?._id ??
              null,
            ];
          } else {
            optionTexts =
              apiOptions.length > 0
                ? apiOptions.map(
                  (option) =>
                    option?.text ??
                    ""
                )
                : getOptionsForQuestionType(
                  questionTypeId
                );

            optionIds =
              apiOptions.length > 0
                ? apiOptions.map(
                  (option) =>
                    option?._id ??
                    null
                )
                : optionTexts.map(
                  () => null
                );
          }

          const correctAnswers =
            apiOptions
              .map(
                (
                  option,
                  index
                ) =>
                  option?.isCorrect
                    ? index
                    : null
              )
              .filter(
                (index) =>
                  index !== null
              );

          const quizData = {
            id:
              quizIdCounter++,

            quiz:
              String(
                questionText
              ),

            questionId: questionId,

            questionTypeId:
              questionTypeId,

            options:
              optionTexts,

            optionWeightages:
              optionTexts.map(
                (_, index) => String(apiOptions[index]?.weightage ?? "")
              ),

            optionIds:
              optionIds,

            correctAnswers:
              correctAnswers,

            isOpen:
              true,

            isNew: false,
          };

          setQuizzes([
            quizData,
          ]);

          setWeightage(
            questionData.weightage ??
            questionData.points ??
            ""
          );
        } catch (error) {
          console.error(
            "Error fetching question:",
            error
          );

          const apiMessage =
            error?.response?.data
              ?.message;

          message.error(
            apiMessage ||
            "Failed to load question data."
          );
        } finally {
          setLoadingEdit(false);
        }
      };

    loadQuestion();
  }, [
    editMode,
    questionId,
    roleId,
    accountId,
    questionTypes,
  ]);

  /* =========================================================
     TOGGLE QUIZ
     ========================================================= */

  const toggleQuiz = (
    id
  ) => {
    setQuizzes(
      (prev) =>
        prev.map(
          (q) =>
            q.id === id
              ? {
                ...q,
                isOpen:
                  !q.isOpen,
              }
              : q
        )
    );
  };

  /* =========================================================
     CLEAR ERROR
     ========================================================= */

  const clearError = (
    quizIndex,
    field
  ) => {
    setErrors(
      (prev) => {
        if (
          !prev.quizzes[
          quizIndex
          ]
        ) {
          return prev;
        }

        const updated =
          [
            ...prev.quizzes,
          ];

        const errorField =
          field === "questionTypeId"
            ? "questionType"
            : field;

        updated[
          quizIndex
        ] = {
          ...updated[
          quizIndex
          ],

          [errorField]:
            errorField ===
              "options"
              ? updated[
                quizIndex
              ].options.map(
                () => ""
              )
              : "",
        };

        return {
          ...prev,
          quizzes:
            updated,
        };
      }
    );
  };

  /* =========================================================
     UPDATE QUIZ FIELD
     ========================================================= */

  const updateQuizField = (
    quizIndex,
    field,
    value
  ) => {
    setQuizzes(
      (prev) =>
        prev.map(
          (
            q,
            index
          ) =>
            index ===
              quizIndex
              ? {
                ...q,
                [field]:
                  value,
              }
              : q
        )
    );

    clearError(
      quizIndex,
      field
    );

    /*
     * Question type changed:
     * rebuild options according
     * to the selected backend type.
     */
    if (field === "questionTypeId") {
      const newOptions =
        getOptionsForQuestionType(value);

      setQuizzes((prev) =>
        prev.map((q, index) =>
          index === quizIndex
            ? {
              ...q,
              questionTypeId: value,
              options: newOptions,
              optionWeightages: newOptions.map(() => ""),
              optionIds: newOptions.map(() => null),
              correctAnswers: [],
            }
            : q
        )
      );
    }
  };

  /* =========================================================
     UPDATE OPTION
     ========================================================= */

  const updateOption = (
    quizIndex,
    optIndex,
    value
  ) => {
    setQuizzes(
      (prev) =>
        prev.map(
          (
            q,
            index
          ) => {
            if (
              index !==
              quizIndex
            ) {
              return q;
            }

            const updatedOptions =
              [
                ...q.options,
              ];

            updatedOptions[
              optIndex
            ] = value;

            return {
              ...q,
              options:
                updatedOptions,
            };
          }
        )
    );

    setErrors(
      (prev) => {
        const quizErr =
          prev.quizzes[
          quizIndex
          ];

        if (
          !quizErr ||
          !quizErr.options ||
          !quizErr.options[
          optIndex
          ]
        ) {
          return prev;
        }

        const updated =
          [
            ...prev.quizzes,
          ];

        const updatedOptions =
          [
            ...updated[
              quizIndex
            ].options,
          ];

        updatedOptions[
          optIndex
        ] = "";

        updated[
          quizIndex
        ] = {
          ...updated[
          quizIndex
          ],
          options:
            updatedOptions,
        };

        return {
          ...prev,
          quizzes:
            updated,
        };
      }
    );
  };

  const updateOptionWeightage = (quizIndex, optionIndex, value) => {
    setQuizzes((prev) =>
      prev.map((quiz, index) =>
        index === quizIndex
          ? {
            ...quiz,
            optionWeightages: (quiz.optionWeightages || []).map(
              (weightage, index2) =>
                index2 === optionIndex ? value : weightage
            ),
          }
          : quiz
      )
    );

    setErrors((prev) => {
      const quizErrors = prev.quizzes[quizIndex];
      if (!quizErrors?.optionWeightages?.[optionIndex]) {
        return prev;
      }

      const updatedQuizzes = [...prev.quizzes];
      updatedQuizzes[quizIndex] = {
        ...quizErrors,
        optionWeightages: quizErrors.optionWeightages.map(
          (error, index) => index === optionIndex ? "" : error
        ),
      };

      return { ...prev, quizzes: updatedQuizzes };
    });
  };

  /* =========================================================
     ADD OPTION
     ========================================================= */

  const addOption = (
    quizIndex
  ) => {
    const quiz =
      quizzes[
      quizIndex
      ];

    if (
      getQuestionTypeValue(
        quiz?.questionTypeId
      ) === "YES_NO"
    ) {
      return;
    }

    setQuizzes(
      (prev) =>
        prev.map(
          (
            q,
            index
          ) =>
            index ===
              quizIndex
              ? {
                ...q,

                options: [
                  ...q.options,
                  "",
                ],

                optionIds: [
                  ...(q.optionIds ||
                    []),
                  null,
                ],

                optionWeightages: [
                  ...(q.optionWeightages || []),
                  "",
                ],
              }
              : q
        )
    );

    setErrors(
      (prev) => {
        const updated =
          [
            ...prev.quizzes,
          ];

        updated[
          quizIndex
        ] = {
          ...updated[
          quizIndex
          ],

          options: [
            ...(updated[
              quizIndex
            ]?.options ||
              []),
            "",
          ],
        };

        return {
          ...prev,
          quizzes:
            updated,
        };
      }
    );
  };

  /* =========================================================
     REMOVE OPTION
     ========================================================= */

  // const removeOption = (
  //   quizIndex,
  //   optIndex
  // ) => {
  //   const quiz =
  //     quizzes[
  //       quizIndex
  //     ];

  //   if (
  //     getQuestionTypeValue(
  //       quiz?.questionTypeId
  //     ) === "YES_NO"
  //   ) {
  //     return;
  //   }

  //   setQuizzes(
  //     (prev) =>
  //       prev.map(
  //         (
  //           q,
  //           index
  //         ) => {
  //           if (
  //             index !==
  //             quizIndex
  //           ) {
  //             return q;
  //           }

  //           if (
  //             q.options.length <=
  //             4
  //           ) {
  //             return q;
  //           }

  //           const updatedOptions =
  //             q.options.filter(
  //               (
  //                 _,
  //                 itemIndex
  //               ) =>
  //                 itemIndex !==
  //                 optIndex
  //             );

  //           const updatedIds =
  //             (
  //               q.optionIds ||
  //               []
  //             ).filter(
  //               (
  //                 _,
  //                 itemIndex
  //               ) =>
  //                 itemIndex !==
  //                 optIndex
  //             );

  //           const updatedCorrectAnswers =
  //             (
  //               q.correctAnswers ||
  //               []
  //             )
  //               .filter(
  //                 (
  //                   answerIndex
  //                 ) =>
  //                   answerIndex !==
  //                   optIndex
  //               )
  //               .map(
  //                 (
  //                   answerIndex
  //                 ) =>
  //                   answerIndex >
  //                   optIndex
  //                     ? answerIndex -
  //                       1
  //                     : answerIndex
  //               );

  //           return {
  //             ...q,

  //             options:
  //               updatedOptions,

  //             optionIds:
  //               updatedIds,

  //             correctAnswers:
  //               updatedCorrectAnswers,
  //           };
  //         }
  //       )
  //   );

  //   setErrors(
  //     (prev) => {
  //       const updated =
  //         [
  //           ...prev.quizzes,
  //         ];

  //       if (
  //         updated[
  //           quizIndex
  //         ]
  //       ) {
  //         updated[
  //           quizIndex
  //         ] = {
  //           ...updated[
  //             quizIndex
  //           ],

  //           options:
  //             updated[
  //               quizIndex
  //             ].options.filter(
  //               (
  //                 _,
  //                 index
  //               ) =>
  //                 index !==
  //                 optIndex
  //             ),
  //         };
  //       }

  //       return {
  //         ...prev,
  //         quizzes:
  //           updated,
  //       };
  //     }
  //   );
  // };

  /* =========================================================
     SINGLE SELECT ANSWER
     ========================================================= */

  const handleSingleCorrectAnswer =
    (
      quizIndex,
      optIndex
    ) => {
      setQuizzes(
        (prev) =>
          prev.map(
            (
              q,
              index
            ) =>
              index ===
                quizIndex
                ? {
                  ...q,

                  correctAnswers:
                    [
                      optIndex,
                    ],
                }
                : q
          )
      );

      clearError(
        quizIndex,
        "correctAnswer"
      );
    };

  /* =========================================================
     MULTI SELECT ANSWER
     ========================================================= */

  const handleMultipleCorrectAnswer =
    (
      quizIndex,
      optIndex
    ) => {
      setQuizzes(
        (prev) =>
          prev.map(
            (
              q,
              index
            ) => {
              if (
                index !==
                quizIndex
              ) {
                return q;
              }

              const current =
                Array.isArray(
                  q.correctAnswers
                )
                  ? q.correctAnswers
                  : [];

              const exists =
                current.includes(
                  optIndex
                );

              const updated =
                exists
                  ? current.filter(
                    (item) =>
                      item !==
                      optIndex
                  )
                  : [
                    ...current,
                    optIndex,
                  ];

              return {
                ...q,

                correctAnswers:
                  updated,
              };
            }
          )
      );

      clearError(
        quizIndex,
        "correctAnswer"
      );
    };

  /* =========================================================
     QUIZ COMPLETE
     ========================================================= */

  const isQuizComplete =
    (q) => {
      if (
        q.quiz.trim() ===
        ""
      ) {
        return false;
      }

      if (
        !q.questionTypeId
      ) {
        return false;
      }

      if (
        !q.options.length ||
        q.options.some(
          (opt) =>
            opt.trim() ===
            ""
        )
      ) {
        return false;
      }

      return true;
    };

  /* =========================================================
     ADD QUIZ
     ========================================================= */

  const handleAddQuiz =
    () => {
      const lastQuiz =
        quizzes[
        quizzes.length -
        1
        ];

      if (
        !isQuizComplete(
          lastQuiz
        )
      ) {
        message.warning(
          "Please fill all the mandatory fields before adding another quiz."
        );

        validateForm();

        return;
      }

      setQuizzes(
        (prev) => {
          const updated =
            prev.map(
              (quiz) => ({
                ...quiz,
                isOpen:
                  false,
              })
            );

          return [
            ...updated,

            {
              ...makeEmptyQuiz(),

              isOpen:
                true,
            },
          ];
        }
      );
    };

  /* =========================================================
     REMOVE QUIZ
     ========================================================= */

  const handleRemoveQuiz =
    (quizIndex) => {
      setQuizzes(
        (prev) =>
          prev.filter(
            (
              _,
              index
            ) =>
              index !==
              quizIndex
          )
      );

      setErrors(
        (prev) => ({
          ...prev,

          quizzes:
            prev.quizzes.filter(
              (
                _,
                index
              ) =>
                index !==
                quizIndex
            ),
        })
      );
    };

  /* =========================================================
     VALIDATE
     ========================================================= */

  const validateForm =
    () => {
      const newQuizErrors =
        quizzes.map(
          (q) => {
            return {
              quiz:
                q.quiz.trim() ===
                  ""
                  ? "Quiz Question is required"
                  : "",

              questionType:
                !q.questionTypeId
                  ? "Question Type is required"
                  : "",

              minimumOptions:
                q.options.length < 2
                  ? "At least two options are required."
                  : "",

              options:
                q.options.map(
                  (opt) =>
                    opt.trim() ===
                      ""
                      ? "This option is required"
                      : ""
                ),

              optionWeightages:
                q.optionWeightages.map(
                  (value) =>
                    value === "" || !Number.isFinite(Number(value)) || Number(value) <= 0
                      ? "Weightage must be greater than 0"
                      : ""
                ),
            };
          }
        );

      const newWeightageError =
        weightage ===
          ""
          ? "Weightage is required"
          : Number(
            weightage
          ) <= 0
            ? "Weightage must be greater than 0"
            : "";

      setErrors({
        quizzes:
          newQuizErrors,
      });

      setWeightageError(
        newWeightageError
      );

      const hasQuizError =
        newQuizErrors.some(
          (qErr) =>
            qErr.quiz ||
            qErr.questionType ||
            qErr.minimumOptions ||
            qErr.options.some(
              (error) =>
                error !== ""
            ) ||
            qErr.optionWeightages.some(
              (error) => error !== ""
            )
        );

      return (
        !hasQuizError &&
        !newWeightageError
      );
    };

  /* =========================================================
     BUILD BACKEND QUESTION PAYLOAD
     ========================================================= */

  const buildQuestionPayload = (quiz) => {
    const typeValue = getQuestionTypeValue(
      quiz.questionTypeId
    );

    const options =
      typeValue === "YES_NO"
        ? ["True", "False"]
        : quiz.options;

    return {
      title: quiz.quiz.trim(),

      questionTypeId: Number(
        quiz.questionTypeId
      ),

      weightage: Number(weightage),

      options: options.map(
        (option, index) => {
          const optionPayload = {
            text: option.trim(),

            weightage: Number(
              quiz.optionWeightages[index]
            ),
          };

          if (quiz.optionIds?.[index]) {
            optionPayload._id =
              quiz.optionIds[index];
          }

          return optionPayload;
        }
      ),
    };
  };

  /* =========================================================
     APPLY BACKEND VALIDATION ERRORS
     ========================================================= */

  const applyBackendValidationErrors = (
    backendErrors
  ) => {
    if (!backendErrors) {
      return false;
    }

    setErrors((prev) => {
      const updatedQuizzes =
        quizzes.map(
          (
            quiz,
            quizIndex
          ) => {
            const currentErrors =
              prev.quizzes[
              quizIndex
              ] || {
                quiz: "",
                questionType:
                  "",
                correctAnswer:
                  "",
                options:
                  quiz.options.map(
                    () => ""
                  ),
              };

            const nextErrors = {
              ...currentErrors,

              options:
                quiz.options.map(
                  (
                    _,
                    optionIndex
                  ) =>
                    currentErrors
                      .options?.[
                    optionIndex
                    ] || ""
                ),
            };

            const source =
              Array.isArray(
                backendErrors
              )
                ? backendErrors[
                quizIndex
                ] || {}
                : backendErrors;

            if (
              typeof source ===
              "string"
            ) {
              nextErrors.quiz =
                source;

              return nextErrors;
            }

            if (
              source &&
              typeof source ===
              "object"
            ) {
              nextErrors.quiz =
                source.question ||
                source.quiz ||
                source.questionText ||
                nextErrors.quiz;

              nextErrors.questionType =
                source.questionTypeId ||
                source.questionType ||
                nextErrors.questionType;

              nextErrors.correctAnswer =
                source.correctAnswer ||
                source.correctAnswers ||
                source.isCorrect ||
                nextErrors.correctAnswer;

              if (
                source.options
              ) {
                if (
                  Array.isArray(
                    source.options
                  )
                ) {
                  nextErrors.options =
                    quiz.options.map(
                      (
                        _,
                        optionIndex
                      ) => {
                        const optionError =
                          source.options[
                          optionIndex
                          ];

                        if (
                          typeof optionError ===
                          "string"
                        ) {
                          return optionError;
                        }

                        if (
                          optionError &&
                          typeof optionError ===
                          "object"
                        ) {
                          return (
                            optionError.text ||
                            optionError.option ||
                            optionError.message ||
                            ""
                          );
                        }

                        return "";
                      }
                    );
                } else if (
                  typeof source.options ===
                  "string"
                ) {
                  nextErrors.options =
                    quiz.options.map(
                      () =>
                        source.options
                    );
                }
              }
            }

            return nextErrors;
          }
        );

      return {
        ...prev,
        quizzes:
          updatedQuizzes,
      };
    });

    return true;
  };

  /* =========================================================
     SAVE
     ========================================================= */

  const handleSave = async () => {
    const isValid = validateForm();

    if (!isValid) {
      message.destroy();

      message.warning(
        "Fill all mandatory fields."
      );

      return;
    }

    if (!roleId) {
      message.error(
        "Role ID is missing. Cannot save."
      );
      return;
    }

    if (!accountId) {
      message.error(
        "Admin client ID is missing. Cannot save."
      );
      return;
    }

    setSaving(true);

    try {
      const adminRoleId = Number(roleId);
      const adminClientId = Number(accountId);

      /*
       * =====================================================
       * SAVE EACH QUIZ
       * =====================================================
       *
       * Existing quiz  -> UPDATE API
       * New quiz       -> CREATE API
       */

      for (const quiz of quizzes) {
        const payload = buildQuestionPayload(quiz);

        payload.isActive = isActive;

        /*
         * Existing quiz
         */
        if (
          editMode &&
          !quiz.isNew &&
          quiz.questionId
        ) {
          await updateDemographicQuestion(
            adminRoleId,
            quiz.questionId,
            payload,
            {
              adminClientId,
            }
          );
        }

        /*
         * New quiz
         */
        else {
          await createDemographicQuestion(
            adminRoleId,
            payload,
            {
              adminClientId,
            }
          );
        }
      }

      message.success(
        quizzes.length > 1
          ? "Quizzes Saved Successfully"
          : editMode
            ? "Quiz Updated Successfully"
            : "Quiz Saved Successfully"
      );

      navigate(-1);

    } catch (error) {
      console.error(
        "Error saving quiz:",
        error
      );

      const apiMessage =
        error?.response?.data?.message;

      const backendErrors =
        error?.response?.data?.errors;

      if (backendErrors) {
        applyBackendValidationErrors(
          backendErrors
        );
      }

      message.error(
        apiMessage ||
        "Failed to save quiz. Please try again."
      );

    } finally {
      setSaving(false);
    }
  };
  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="
      min-h-screen
      bg-[#F6F6F6]
    ">
      <ReusableHeader />

      <div className="demographic-quiz-form px-8 pb-8 pt-3">
        {/* BACK */}

        <button
          onClick={() =>
            navigate(-1)
          }

          className="
            flex
            items-center
            gap-2
            text-[#D50000]
            text-sm
            font-medium
            mt-[-17px]
          "
        >
          <FaArrowLeft />

          Back
        </button>

        {/* TITLE */}

        <div className="
          flex
          items-center
          justify-between
          mb-1
          mt-2
        ">
          <h2 className="
            text-xl
            font-semibold
          ">
            {
              editMode
                ? "Edit Demographic Quiz"
                : "Add Demographic Quiz"
            }
          </h2>

          {editMode && (
            <div className="
              flex
              items-center
              gap-2
            ">
              <Switch
                checked={isActive}
                onChange={setIsActive}
              />

              <span className="
                text-[14px]
                font-medium
              ">
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>
          )}
        </div>

        {loadingEdit ? (
          <div className="
            flex
            justify-center
            items-center
            h-[300px]
          ">
            <Spin
              size="large"
            />
          </div>
        ) : (
          <>
            {quizzes.map(
              (
                q,
                qIndex
              ) => {
                const quizErr =
                  errors.quizzes[
                  qIndex
                  ] || {
                    quiz: "",
                    questionType:
                      "",
                    weightage:
                      "",
                    correctAnswer:
                      "",
                    options:
                      [],
                  };

                const selectedType =
                  getQuestionTypeById(
                    q.questionTypeId
                  );

                const typeValue =
                  selectedType?.value;

                const isMultiSelect =
                  typeValue ===
                  "MULTI_SELECT";

                const isYesNo =
                  typeValue === "YES_NO" ||
                  selectedType?.name
                    ?.trim()
                    .toUpperCase()
                    .replace(/\s+/g, "_")
                    .replace(/\//g, "_") === "YES_NO";

                return (
                  <div
                    key={q.id}
                    className="
                      bg-white
                      rounded-lg
                      border
                      mt-3
                      p-6
                    "
                  >
                    <div className="
                      flex
                      justify-between
                      items-center
                    ">
                      <h3 className="
                        text-base
                        font-semibold
                      ">
                        Quiz Question{" "}
                        {qIndex + 1}
                      </h3>

                      <div className="
                        flex
                        items-center
                        gap-3
                      ">
                        {!q.isOpen &&
                          quizzes.length >
                          1 && (
                            <button
                              type="button"

                              onClick={() =>
                                handleRemoveQuiz(
                                  qIndex
                                )
                              }

                              className="
                                border
                                border-red-600
                                text-red-600
                                px-4
                                py-1
                                rounded-md
                                text-sm
                                hover:bg-red-50
                              "
                            >
                              Delete
                            </button>
                          )}

                        <button
                          type="button"

                          onClick={() =>
                            toggleQuiz(
                              q.id
                            )
                          }

                          className="
                            w-8
                            h-8
                            rounded-full
                            bg-[#FDEAEA]
                            flex
                            items-center
                            justify-center
                          "
                        >
                          {q.isOpen ? (
                            <ChevronUp
                              className="
      w-4
      h-4
      text-[#D50000]
    "
                            />
                          ) : (
                            <ChevronDown
                              className="
      w-4
      h-4
      text-[#D50000]
    "
                            />
                          )}
                        </button>
                      </div>
                    </div>

                    {q.isOpen && (
                      <>
                        {/* QUESTION + TYPE */}

                        <div className="
                          grid
                          grid-cols-2
                          gap-x-5
                          gap-y-3
                          mt-4
                        ">
                          {/* QUESTION */}

                          <div>
                            <label className="
                              block
                              mb-1
                              text-sm
                              font-medium
                            ">
                              Quiz{" "}
                              <span className="
                                text-red-600
                              ">
                                *
                              </span>
                            </label>

                            <input
                              value={
                                q.quiz
                              }

                              onChange={(e) =>
                                updateQuizField(
                                  qIndex,
                                  "quiz",
                                  e.target.value
                                )
                              }

                              placeholder="
                                Enter Quiz Question
                              "

                              className={`
                                w-full
                                h-10
                                rounded-md
                                px-3
                                text-sm
                                border

                                ${quizErr.quiz
                                  ? "border-red-500"
                                  : "border-gray-300"
                                }
                              `}
                            />

                            {quizErr.quiz && (
                              <p className="
                                text-red-500
                                text-sm
                                mt-1
                              ">
                                {
                                  quizErr.quiz
                                }
                              </p>
                            )}
                          </div>

                          {/* QUESTION TYPE */}

                          <div>
                            <label className="
                              block
                              mb-1
                              text-sm
                              font-medium
                            ">
                              Question Type

                              <span className="
                                text-red-600
                              ">
                                *
                              </span>
                            </label>

                            <Select
                              showSearch

                              className="
                                w-full
                                custom-select
                              "

                              placeholder="
                                Select Question Type
                              "

                              loading={
                                loadingOptions
                              }

                              disabled={editMode && !q.isNew}

                              value={
                                q.questionTypeId ||
                                undefined
                              }

                              optionFilterProp="
                                label
                              "

                              filterOption={(
                                input,
                                option
                              ) =>
                                (
                                  option?.label ||
                                  ""
                                )
                                  .toLowerCase()
                                  .includes(
                                    input.toLowerCase()
                                  )
                              }

                              options={
                                questionTypes.map(
                                  (
                                    type
                                  ) => ({
                                    value:
                                      type.id,

                                    label:
                                      type.name,
                                  })
                                )
                              }

                              onChange={(
                                value
                              ) =>
                                updateQuizField(
                                  qIndex,
                                  "questionTypeId",
                                  value
                                )
                              }

                              status={
                                quizErr.questionType
                                  ? "error"
                                  : ""
                              }
                            />

                            {quizErr.questionType && (
                              <p className="
                                text-red-500
                                text-sm
                                mt-1
                              ">
                                {
                                  quizErr.questionType
                                }
                              </p>
                            )}
                          </div>
                        </div>

                        <hr className="
                          my-5
                        " />

                        {/* OPTIONS HEADER */}

                        <div
                          className="
    flex
    justify-between
    items-center
  "
                        >
                          <h3
                            className="
      text-lg
      font-bold
    "
                          >
                            Options
                          </h3>

                          {!isYesNo && (
                            <button
                              type="button"
                              onClick={() => addOption(qIndex)}
                              className="
        text-[#AD0101]
        font-semibold
        text-xs
      "
                            >
                              Add Option
                            </button>
                          )}
                        </div>

                        {/* OPTIONS */}

                        <div
                          className="
    grid
    grid-cols-2
    gap-x-5
    gap-y-3
    mt-3
  "
                        >
                          {q.options.map((option, optIndex) => (
                            <div
                              key={optIndex}
                              className="grid grid-cols-[minmax(0,1fr)_100px] gap-3 items-end"
                            >
                              <div>
                                <label
                                  className="
          block
          font-medium
          mb-1
          text-sm
        "
                                >
                                  Option {optIndex + 1}

                                  <span className="text-red-600">
                                    *
                                  </span>
                                </label>

                                <div className="relative">
                                  <input
                                    type="text"
                                    value={option}
                                    disabled={isYesNo}
                                    placeholder={
                                      isYesNo
                                        ? ""
                                        : `Enter Option ${optIndex + 1}`
                                    }
                                    onChange={(e) =>
                                      updateOption(
                                        qIndex,
                                        optIndex,
                                        e.target.value
                                      )
                                    }
                                    className={`
            w-full
            h-10
            rounded-[6px]
            px-3
            text-sm
            border
            outline-none

            ${!isYesNo &&
                                        q.options.length > 2
                                        ? "pr-10"
                                        : ""
                                      }

            ${isYesNo
                                        ? "bg-gray-100 cursor-not-allowed"
                                        : ""
                                      }

            ${quizErr.options?.[optIndex]
                                        ? "border-red-500"
                                        : "border-gray-300"
                                      }
          `}
                                  />

                                  {/* DELETE ICON:
            Show only when:
            1. Not Yes/No question
            2. More than 2 options exist
        */}
                                  {!isYesNo &&
                                    q.options.length > 2 && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeOption(
                                            qIndex,
                                            optIndex
                                          )
                                        }
                                        className="
                absolute
                right-2
                top-1/2
                -translate-y-1/2
                flex
                items-center
                justify-center
                bg-transparent
                border-none
                cursor-pointer
                p-0
                z-10
              "
                                      >
                                        <img
                                          src={DeleteIcon}
                                          alt="Delete"
                                          className="
                  w-[16px]
                  h-[16px]
                  object-contain
                "
                                        />
                                      </button>
                                    )}
                                </div>

                                {quizErr.options?.[optIndex] && (
                                  <p
                                    className="
                                    text-red-500
                                    text-sm
                                    mt-1"
                                  >
                                    {quizErr.options[optIndex]}
                                  </p>
                                )}
                              </div>
                              <div>
                                <label className="block font-medium mb-1 text-sm">
                                  Weightage{" "}
                                  <span className="text-red-600">*</span>
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  value={q.optionWeightages?.[optIndex] ?? ""}
                                  onChange={(e) =>
                                    updateOptionWeightage(qIndex, optIndex, e.target.value)
                                  }
                                  className={`w-full h-10 rounded-[6px] px-3 text-sm border outline-none ${quizErr.optionWeightages?.[optIndex]
                                      ? "border-red-500"
                                      : "border-gray-300"
                                    }`}
                                />
                                {quizErr.optionWeightages?.[optIndex] && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {quizErr.optionWeightages[optIndex]}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {quizErr.minimumOptions && (
                          <p className="text-red-500 text-sm mt-2">
                            {quizErr.minimumOptions}
                          </p>
                        )}

                        {/* CORRECT ANSWER */}

                        {/* <div className="
                          mt-4
                        ">
                          <h3 className="
                            text-base
                            font-semibold
                            mb-3
                          ">
                            Correct Answer

                            <span className="
                              text-red-600
                            ">
                              *
                            </span>
                          </h3>

                          <div className="
                            flex
                            flex-wrap
                            items-center
                            gap-5
                          ">
                            {q.options.map(
                              (
                                option,
                                optIndex
                              ) => (
                                <label
                                  key={
                                    optIndex
                                  }

                                  className="
                                    flex
                                    items-center
                                    gap-2
                                    cursor-pointer
                                  "
                                >
                                  {isMultiSelect ? (
                                    <input
                                      type="checkbox"

                                      checked={
                                        (
                                          q.correctAnswers ||
                                          []
                                        ).includes(
                                          optIndex
                                        )
                                      }

                                      onChange={() =>
                                        handleMultipleCorrectAnswer(
                                          qIndex,
                                          optIndex
                                        )
                                      }

                                      className="
                                        w-4
                                        h-4
                                        accent-[#AD0101]
                                      "
                                    />
                                  ) : (
                                    <input
                                      type="radio"

                                      name={`correctAnswer-${q.id}`}

                                      checked={
                                        (
                                          q.correctAnswers ||
                                          []
                                        ).includes(
                                          optIndex
                                        )
                                      }

                                      onChange={() =>
                                        handleSingleCorrectAnswer(
                                          qIndex,
                                          optIndex
                                        )
                                      }

                                      className="
                                        w-4
                                        h-4
                                        accent-[#AD0101]
                                      "
                                    />
                                  )}

                                  <span className="
                                    text-[13px]
                                    text-[#3A3A3A]
                                  ">
                                    {isYesNo
                                      ? option
                                      : `Option ${optIndex +
                                      1
                                      }`}
                                  </span>
                                </label>
                              )
                            )}
                          </div>

                          {quizErr.correctAnswer && (
                            <p className="
                              text-red-500
                              text-sm
                              mt-3
                            ">
                              {
                                quizErr.correctAnswer
                              }
                            </p>
                          )}
                        </div> */}
                      </>
                    )}
                  </div>
                );
              }
            )}

            {/* WEIGHTAGE */}

            <div className="
              bg-white
              rounded-lg
              border
              mt-3
              p-6
            ">
              <h3 className="
                text-lg
                font-semibold
                mb-4
              ">
                Weightage (Points)
              </h3>

              <div className="
                w-full
                max-w-[320px]
              ">
                <label className="
                  block
                  mb-1
                  text-sm
                  font-medium
                ">
                  Weightage

                  <span className="
                    text-red-600
                  ">
                    *
                  </span>
                </label>


                <div className="weightage-number-input">
                  <input
                    className={`quiz-weightage w-full border rounded-[6px] px-3 pr-10 outline-none text-sm ${errors.weightage
                      ? "border-red-600"
                      : "border-gray-300"
                      }`}
                    type="number"
                    min="1"
                    placeholder="Enter Weightage"
                    value={weightage}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                      setWeightage(e.target.value);
                      clearError("weightage");
                    }}
                  />
                  <div className="weightage-number-stepper">
                    <button
                      type="button"
                      aria-label="Increase weightage"
                      onClick={() => {
                        setWeightage(String(Math.max(1, Number(weightage) || 0) + 1));
                        clearError("weightage");
                      }}
                    >
                      <span className="weightage-stepper-arrow weightage-stepper-arrow-up" />
                    </button>
                    <button
                      type="button"
                      aria-label="Decrease weightage"
                      onClick={() => {
                        setWeightage(String(Math.max(1, (Number(weightage) || 1) - 1)));
                        clearError("weightage");
                      }}
                    >
                      <span className="weightage-stepper-arrow weightage-stepper-arrow-down" />
                    </button>
                  </div>
                </div>

                {errors.weightage && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.weightage}
                  </p>
                )}
              </div>
            </div>



            {/* FOOTER */}

            <div className="
              flex
              justify-between
              items-center
              mt-5
            ">
              <button
                type="button"

                onClick={
                  handleAddQuiz
                }

                className="
                  border
                  border-[#AD0101]
                  text-[#AD0101]
                  h-8
                  px-3
                  rounded-[6px]
                  text-xs
                  font-medium
                  hover:bg-red-50
                  transition-all
                "
              >
                Add Quiz
              </button>

              <div className="
                flex
                gap-4
              ">
                <button
                  type="button"

                  onClick={() =>
                    navigate(-1)
                  }

                  className="
                    px-4
                    py-2
                    rounded-[6px]
                    border
                    border-gray-300
                    text-xs
                    font-medium
                  "
                >
                  Cancel
                </button>

                <button
                  type="button"

                  onClick={
                    handleSave
                  }

                  disabled={
                    saving
                  }

                  className="
                    px-4
                    py-2
                    rounded-[6px]
                    bg-[#AD0101]
                    text-white
                    text-xs
                    font-medium
                    hover:bg-red-700
                    disabled:opacity-50
                  "
                >
                  {
                    saving
                      ? "Saving..."
                      : editMode
                        ? "Update"
                        : "Save"
                  }
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddQuize;